/**
 * Scratching.
 *
 * Dragging the record keeps the groove under the finger and seeks the audio to
 * match. Seeks are throttled — YouTube in particular cannot take sixty a
 * second — while the visual keeps up with every move.
 */

import type * as THREE from 'three';
import { DIM } from '../geometry/dims.ts';
import { angleDelta, discAngle, scratchSeconds } from '../geometry/transport.ts';
import { clamp } from '../lib/math.ts';
import * as player from '../state/player.ts';
import { hardware } from './hardware.ts';

/** Milliseconds between seeks, per engine. */
const THROTTLE = { demo: 60, youtube: 140 } as const;

interface ScratchSession {
  lastAngle: number;
  /** Total rotation applied since the finger went down, in radians. */
  accumulated: number;
  basePosition: number;
  wasPlaying: boolean;
  lastSeek: number;
}

let session: ScratchSession | null = null;

export function beginScratch(point: THREE.Vector3): void {
  const { tt } = hardware();
  session = {
    lastAngle: discAngle(point.x, point.z),
    accumulated: 0,
    basePosition: player.deck.position,
    wasPlaying: player.deck.playing,
    lastSeek: 0,
  };
  if (player.deck.playing) player.deck.pause();
  tt.setSpinning(false);
}

/** The audio position a given amount of accumulated rotation lands on. */
function positionFor(s: ScratchSession): number {
  const { tt } = hardware();
  const seconds = scratchSeconds(s.accumulated, tt.state.rpm);
  return clamp(s.basePosition + seconds, 0, player.deck.duration || 0);
}

export function moveScratch(point: THREE.Vector3): void {
  const s = session;
  if (!s) return;
  const { tt } = hardware();

  const angle = discAngle(point.x, point.z);
  const delta = angleDelta(angle, s.lastAngle);
  s.lastAngle = angle;
  s.accumulated += delta;
  tt.nudgeAngle(-delta); /* keep the groove under the finger */

  const pos = positionFor(s);
  const now = performance.now();
  if (now - s.lastSeek > THROTTLE[player.deck.source]) {
    s.lastSeek = now;
    player.deck.seek(pos);
  }
  player.setPosition(pos);
  const d = player.deck.duration;
  if (d) tt.setProgress(pos / d);
}

export function endScratch(): void {
  const s = session;
  session = null;
  if (!s) return;

  player.deck.seek(positionFor(s));
  if (s.wasPlaying) {
    hardware().tt.setSpinning(true);
    void player.deck.play();
  }
}

/** The plane a scratch gesture is tracked on: the top face of the record. */
export const SCRATCH_PLANE_Y = DIM.recordTop;
